import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ApiKeyList, CreateApiKeyRequest, UpdateApiKeyRequest } from '@clientwire/api';
import { ResponseError, WireApiService } from '@frontend/shared';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class ApiKeysService {
  readonly #wireApiService = inject(WireApiService);
  readonly #translateService = inject(TranslateService);
  readonly #toastr = inject(ToastrService);

  error: WritableSignal<string | undefined> = signal(undefined);
  newApiKeyToken: WritableSignal<string | undefined> = signal(undefined);

  readonly loading = signal(false);
  readonly apiKeys: WritableSignal<ApiKeyList | null> = signal(null);

  loadApiKeys(): void {
    this.loading.set(true);
    this.#wireApiService.apiKeysApi
      .listApiKeys()
      .then((result: ApiKeyList) => {
        this.apiKeys.set(result);
        this.loading.set(false);
      })
      .catch(() => {
        this.loading.set(false);
      });
  }

  async createApiKey(apiKey: CreateApiKeyRequest) {
    try {
      this.clearErrors();
      const request = {
        name: apiKey.name,
        expiresAt: apiKey.expiresAt,
      };
      const response = await this.#wireApiService.apiKeysApi.createApiKey({
        createApiKeyRequest: request,
      });
      if (response.apiKey) {
        this.newApiKeyToken.set(response.apiKey);
      }
      this.loadApiKeys();
      this.#toastr.success(this.#translateService.instant('SETTINGS.API_KEYS.API_KEY_CREATED'));
      return response;
    } catch (error) {
      await this.handleError(error);
      return null;
    }
  }

  async updateApiKey(apiKey: UpdateApiKeyRequest) {
    try {
      this.clearErrors();
      await this.#wireApiService.apiKeysApi.updateApiKey(apiKey);
      this.loadApiKeys();
      this.#toastr.success(this.#translateService.instant('SETTINGS.API_KEYS.API_KEY_UPDATED'));
    } catch (error) {
      await this.handleError(error);
    }
  }

  async deleteApiKey(apiKeyId: string) {
    try {
      this.clearErrors();
      await this.#wireApiService.apiKeysApi.deleteApiKey({ keyId: apiKeyId });
      this.loadApiKeys();
      this.#toastr.success(this.#translateService.instant('SETTINGS.API_KEYS.API_KEY_DELETED'));
    } catch (error) {
      await this.handleError(error);
    }
  }

  private async handleError(error: unknown): Promise<void> {
    if (error instanceof ResponseError) {
      this.error.set(error.message);
    } else {
      this.error.set(this.#translateService.instant('SHARED.ERRORS.UNKNOWN_ERROR'));
    }
  }

  clearErrors(): void {
    this.error.set(undefined);
    this.newApiKeyToken.set(undefined);
  }
}
